import classNames from 'classnames'
import {Icon} from 'quickstart/components/content/Icon'
import {Link, type LinkProps} from 'quickstart/components/content/Link'
import {Text, type TextProps} from 'quickstart/components/content/Text'
import {DropdownMenu} from 'quickstart/components/controls/DropdownMenu'
import {MetaLink} from 'quickstart/components/tizra/MetaLink'
import {useSearch} from 'quickstart/hooks/useSearch'
import {useSearchConfig} from 'quickstart/hooks/useSearchConfig'
import {wrapChildren} from 'quickstart/utils'
import * as Render from 'quickstart/utils/render'
import type {ComponentProps, CSSProperties, ElementType, ReactNode} from 'react'
import {logger} from 'tizra'
import S from './styles.module.scss'

const log = logger('NavLink')

interface NavLinkLinkOptions extends Render.Options {
  label?: ReactNode
  // catch programming errors
  type?: never
  link?: never
  text?: never
}

export type NavLinkLinkProps<T extends ElementType = typeof Link> =
  Render.Props<T, NavLinkLinkOptions>

export const NavLinkLink = ({children, label, ...props}: NavLinkLinkProps) =>
  Render.createElement(Link, {
    children: children || label || '',
    variant: 'nav',
    ...props,
    className: classNames(S['nav-link-link'], props.className),
  })

export type NavLinkAccountProps = NavLinkLinkProps

const NavLinkAccount = NavLinkLink

export type NavLinkBookshelfProps = NavLinkLinkProps

const NavLinkBookshelf = NavLinkLink

export interface NavLinkExpandoProps {
  metaTypes?: string[]
  adminTag?: string
  sortProp?: string
  sortDirection?: 'ascending' | 'descending'
  variant?: LinkProps['variant'] // ignored
  // catch programming errors
  type?: never
}

const NavLinkExpando = ({
  metaTypes = ['AdminTagged'],
  adminTag = '',
  sortProp = 'Title',
  sortDirection = 'ascending',
}: NavLinkExpandoProps) => {
  const searchConfig = useSearchConfig({
    mode: 'browse',
    metaTypes: {metadata: metaTypes},
    pre: {
      all: [adminTag],
    },
    fields: {
      depth: {defaultValue: 'metadata'},
      page: {defaultValue: 100},
      sort: {defaultValue: `sortProp-${sortDirection}`},
    },
    sorting: {sortProp},
  })
  const {results} = useSearch(searchConfig)
  if (!results) {
    return <DropdownMenu.Item>(none)</DropdownMenu.Item>
  }
  return (
    <>
      {results.map((r, i) => (
        <DropdownMenu.Item key={i} render={<MetaLink metaObj={r} />} />
      ))}
    </>
  )
}

export interface NavLinkMenuProps {
  label?: string
  items?: NavLinkProps[]
  menuProps?: Omit<ComponentProps<typeof DropdownMenu>, 'children' | 'label'>
  itemProps?: {
    style?: CSSProperties
  }
  variant?: LinkProps['variant'] // ignored
  // catch programming errors
  type?: never
}

const NavLinkMenu = ({
  label = '',
  items = [],
  menuProps,
  itemProps,
}: NavLinkMenuProps) => {
  return (
    <DropdownMenu label={label} variant="nav" {...menuProps}>
      {items.map((props, i) =>
        'items' in props || props.type === 'separator' ? (
          <NavLink key={i} variant="unstyled" {...itemProps} {...props} />
        ) : (
          <DropdownMenu.Item
            key={i}
            render={<NavLink variant="unstyled" {...props} />}
            {...itemProps}
          />
        ),
      )}
    </DropdownMenu>
  )
}

export interface NavLinkSeparatorProps {
  variant?: LinkProps['variant'] // ignored
}

const NavLinkSeparator = ({variant, ...props}: NavLinkSeparatorProps) => (
  <DropdownMenu.Separator {...props} />
)

type WithType<T, U> = {type: T} & Omit<U, 'type'>

export type NavLinkAccountPropsWithType = WithType<
  'account',
  NavLinkAccountProps
>
export type NavLinkBookshelfPropsWithType = WithType<
  'bookshelf',
  NavLinkBookshelfProps
>
export type NavLinkExpandoPropsWithType = WithType<
  'expando',
  NavLinkExpandoProps
>
export type NavLinkLinkPropsWithType = WithType<'link', NavLinkLinkProps>
export type NavLinkOutPropsWithType = WithType<'linkOut', NavLinkLinkProps>
export type NavLinkMenuPropsWithType = WithType<'menu', NavLinkMenuProps>
export type NavLinkSeparatorPropsWithType = WithType<
  'separator',
  NavLinkSeparatorProps
>

export type NavLinkProps =
  | NavLinkAccountPropsWithType
  | NavLinkBookshelfPropsWithType
  | NavLinkExpandoPropsWithType
  | NavLinkLinkPropsWithType
  | NavLinkOutPropsWithType
  | NavLinkMenuPropsWithType
  | NavLinkSeparatorPropsWithType

const _NavLink = (props: NavLinkProps) => {
  switch (props.type) {
    case 'account': {
      const {type, ...rest} = props
      return <NavLinkAccount {...rest} />
    }
    case 'bookshelf': {
      const {type, ...rest} = props
      return <NavLinkBookshelf {...rest} />
    }
    case 'expando': {
      const {type, ...rest} = props
      return <NavLinkExpando {...rest} />
    }
    case 'link': {
      const {type, ...rest} = props
      return <NavLinkLink {...rest} />
    }
    case 'linkOut': {
      const {type, children, label, ...rest} = props
      return (
        <NavLinkLink target="_blank" {...rest}>
          {wrapChildren(children || label || '')}
          <Icon icon="externalLink" />
        </NavLinkLink>
      )
    }
    case 'menu': {
      const {type, ...rest} = props
      return <NavLinkMenu {...rest} />
    }
    case 'separator': {
      const {type, ...rest} = props
      return <NavLinkSeparator {...rest} />
    }
    default: {
      const unreachable: never = props
      log.error('unreachable case:', unreachable)
      return (props as any).type ? null : <NavLinkLink {...(props as any)} />
    }
  }
}

interface NavLinkGroupProps extends TextProps {
  alignment?: 'left' | 'center' | 'right'
}

const NavLinkGroup = ({alignment = 'left', ...props}: NavLinkGroupProps) => (
  <Text
    variant="nav"
    {...props}
    className={classNames(S['nav-link-group'], props.className)}
    style={{
      '--alignment':
        alignment === 'left'
          ? 'flex-start'
          : alignment === 'right'
            ? 'flex-end'
            : alignment,
      ...props.style,
    }}
  />
)

export const NavLink = Object.assign(_NavLink, {Group: NavLinkGroup})
